import React from 'react';
import { Typography, Box } from '@material-ui/core';
import { ActiveStep } from '../OnboardingContext';
import CheckCircle from '@material-ui/icons/CheckCircleOutlineOutlined';
import { OnboardingMessageKey } from '../../../translations/messages';
import { OnboardingLanguage } from '../../../translations';
import { OnboardingColors } from '../colors';
import { TFunction } from './journeySteps';
import { PlaygroundStep } from './PlaygroundStep';
import { OnboardingImage } from './OnboardingImage';
import { OnboardingVideo } from './OnboardingVideo';

/* ──────────────────────────────────────────────────────────────
 *  DiagramPlaceholder: A flexible component for showing diagrams
 * ────────────────────────────────────────────────────────────── */

export const DiagramPlaceholder = ({ 
  path, 
  height = 180, 
  colors 
}: { 
  path: string; 
  height?: number;
  colors?: OnboardingColors;
}) => (
  <Box 
    style={{ 
      backgroundColor: colors?.placeholderBg || '#f5f5f5', 
      borderRadius: 8, 
      height, 
      width: '100%', 
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px dashed ${colors?.flowSubtitle || '#999'}`,
    }}
  >
    <Typography variant="caption" style={{ color: colors?.subtitleText || '#666' }}>
      [Diagram: {path}]
    </Typography>
  </Box>
);

/* ================================================================
 *  Step metadata — shared across all languages
 * ================================================================ */

interface StepDef {
  sectionKey: OnboardingMessageKey;
  titleKey: OnboardingMessageKey;
  stepIndex: number;
  totalSteps: number;
  targetSelector?: string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  navigateTo?: string;
  customActions?: boolean;
  hideStepTitle?: boolean;
  centerContent?: boolean;
  stepType?: 'quiz' | 'playground';
}

const journey2StepDefs: StepDef[] = [
  /* PHASE 1: Before you start (2 steps) */
  { sectionKey: 'sectionBeforeStart', titleKey: 'stepTwoRepositories', stepIndex: 1, totalSteps: 2 },
  { sectionKey: 'sectionBeforeStart', titleKey: 'stepPlatformHandles', stepIndex: 2, totalSteps: 2 },

  /* PHASE 2: The CI/CD pipeline (3 steps) */
  { sectionKey: 'sectionCICDPipeline', titleKey: 'stepCompleteFlow', stepIndex: 1, totalSteps: 3 },
  { sectionKey: 'sectionCICDPipeline', titleKey: 'stepPipelineStages', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionCICDPipeline', titleKey: 'stepArgoCDAutoDeploy', stepIndex: 3, totalSteps: 3 },

  /* PHASE 3: Configuring your application (4 steps) */
  { sectionKey: 'sectionConfiguringApp', titleKey: 'stepComponentFile', stepIndex: 1, totalSteps: 4 },
  { sectionKey: 'sectionConfiguringApp', titleKey: 'stepEnvSecrets', stepIndex: 2, totalSteps: 4 },
  { sectionKey: 'sectionConfiguringApp', titleKey: 'stepResourcesHealth', stepIndex: 3, totalSteps: 4 },
  { sectionKey: 'sectionConfiguringApp', titleKey: 'stepValidateYAML', stepIndex: 4, totalSteps: 4 },

  /* PHASE 4: Branches & going live (3 steps) */
  { sectionKey: 'sectionBranchesLive', titleKey: 'stepBranchMapping', stepIndex: 1, totalSteps: 3 },
  { sectionKey: 'sectionBranchesLive', titleKey: 'stepGoingProduction', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionBranchesLive', titleKey: 'stepIndependentlyDo', stepIndex: 3, totalSteps: 3 },

  /* PHASE 5: Monitoring your deploys (3 steps) */
  { sectionKey: 'sectionMonitoringDeploys', titleKey: 'stepCheckingArgoCD', stepIndex: 1, totalSteps: 3 },
  { sectionKey: 'sectionMonitoringDeploys', titleKey: 'stepCheckingLogs', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionMonitoringDeploys', titleKey: 'stepJourneyComplete', stepIndex: 3, totalSteps: 3 },
  /* PHASE 6: Playground (1 step, custom actions) */
  { sectionKey: 'sectionPlayground', titleKey: 'stepPlayground', stepIndex: 1, totalSteps: 1, customActions: true, stepType: 'playground', hideStepTitle: true, centerContent: true },
];

/* ================================================================
 *  Content per language
 * ================================================================ */

type ContentGetter = (c: OnboardingColors) => React.ReactNode[];

const journey2ContentByLanguage: Record<OnboardingLanguage, ContentGetter> = {
  en: (c) => getJourney2ContentEN(c),
  es: (c) => getJourney2ContentES(c),
  'pt-BR': (c) => getJourney2ContentPtBR(c),
};

/* ================================================================
 *  Public API
 * ================================================================ */

export const getJourney2Steps = (
  journeyIndex: number,
  journeyTitle: string,
  t: TFunction = key => key,
  language: OnboardingLanguage = 'en',
  colors?: OnboardingColors,
): ActiveStep[] => {
  const journeyLabel = `${t('journeyLabel', { index: journeyIndex + 1 })}: ${journeyTitle}`;

  switch (journeyIndex) {
    case 1: {
      const getContent = journey2ContentByLanguage[language] ?? journey2ContentByLanguage.en;
      const fallbackColors: OnboardingColors = colors ?? ({} as OnboardingColors);
      const contents = getContent(fallbackColors);

      // Build playground content for the last step
      const playgroundContent = (
        <PlaygroundStep
          applicationDomain="https://onboard.innoams.nestle.biz"
          labels={{
            title: t('playgroundTitle'),
            description: t('playgroundDescription'),
            taskListHeader: t('playgroundTaskListHeader'),
            tasks: [
              t('playgroundTask1'),
              t('playgroundTask2'),
              t('playgroundTask3'),
              t('playgroundTask4'),
              t('playgroundTask5'),
            ],
            tutorialLabel: t('playgroundTutorialLabel'),
            tutorialDescription: t('playgroundTutorialDescription'),
            tutorialUrl: 'https://onboard.innoams.nestle.biz/',
            repoLabel: t('playgroundRepoLabel'),
            repoUrl: 'https://github.com/orgs/nestle-it/repositories?q=playground-onboard',
            verifyHint: t('playgroundVerifyHint'),
            verify: t('playgroundVerify'),
            completed: t('playgroundCompleted'),
            completedMessage: t('playgroundCompletedMessage'),
            nextJourneyUnlocked: t('playgroundNextJourneyUnlocked'),
            continueToJourney: t('playgroundContinueToJourney'),
            failedTitle: t('playgroundFailedTitle'),
            failedMessage: t('playgroundFailedMessage'),
            failedHint: t('playgroundFailedHint'),
            reviewJourney: t('playgroundReviewJourney'),
            verifyAgain: t('playgroundVerifyAgain'),
          }}
        />
      );
      const allContents = [...contents, playgroundContent];

      return journey2StepDefs.map((def, i) => ({
        journeyIndex,
        journeyTitle: journeyLabel,
        sectionTitle: t(def.sectionKey),
        stepIndex: def.stepIndex,
        totalSteps: def.totalSteps,
        stepTitle: t(def.titleKey),
        stepContent: allContents[i],
        ...(def.targetSelector && { targetSelector: def.targetSelector }),
        ...(def.placement && { placement: def.placement }),
        ...(def.navigateTo && { navigateTo: def.navigateTo }),
        ...(def.customActions && { customActions: def.customActions }),
        ...(def.stepType && { stepType: def.stepType }),
        ...(def.hideStepTitle && { hideStepTitle: def.hideStepTitle }),
        ...(def.centerContent && { centerContent: def.centerContent }),
      }));
    }
    default:
      return [];
  }
};

/* ================================================================
 *  Journey 2 — English (15 steps: 2+3+4+3+3)
 * ================================================================ */

const getJourney2ContentEN = (c: OnboardingColors): React.ReactNode[] => [
  /* PHASE 1: Before you start */
  <Box key="en-1">
    <OnboardingImage imagePath="images/Journey%202/Phase%201%20-%20Step%201/Frame.png" alt="Two repositories flow" height={280} />
    <Typography variant="body2" paragraph>
      <strong>Your project uses two repositories that work together:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Application repository (GitHub)</strong><br />
      This is where your code lives. You write features, fix bugs, and push changes here.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>GitOps repository</strong><br />
      This is where the configuration for how your application runs in each environment lives: environment variables, secrets, CPU/memory limits, and health checks.
    </Typography>
    <Typography variant="body2" paragraph>
      The GitOps repo is organized by environment:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="caption">dv/ → Development</Typography><br />
      <Typography variant="caption">ts/ → Test / Homolog</Typography><br />
      <Typography variant="caption">pp/ → Pre-production</Typography><br />
      <Typography variant="caption">pr/ → Production</Typography>
    </Typography>
  </Box>,

  <Box key="en-2">
    <Typography variant="body2" paragraph>
      <strong>Dockerfiles</strong><br />
      Created and maintained by the Platform Team. Not stored in your repo. Even if you add your own Dockerfile, the pipeline will ignore it.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Pipeline templates</strong><br />
      All CI/CD pipelines are pre-configured using templates that support multiple languages. Each app comes with a pipeline configuration ready to use.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Helm charts</strong><br />
      Used by ArgoCD to package and deploy your application. You configure your component file; Helm handles the rest.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Your responsibility: write application code, configure your component files in the GitOps repository, and monitor your deployments.
    </Typography>
  </Box>,

  /* PHASE 2: The CI/CD pipeline */
  <Box key="en-3">
    <OnboardingImage imagePath="images/Journey%202/Phase%202%20-%20Step%201/Frame.png" alt="Complete flow" height={280} />
    <Typography variant="body2" paragraph>
      <strong>Here is what happens end-to-end when you push code:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. You push to the develop branch</strong> in the application repository on GitHub.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>2. The CI pipeline runs automatically</strong> - Three stages execute in sequence: Tests, Build, and GitOps Update.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>3. The GitOps repository is updated</strong> - The pipeline updates the bom.yaml file with the new image version.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>4. ArgoCD detects the change</strong> - ArgoCD automatically deploys the new version to the Kubernetes cluster for the dv environment (Kds).
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      This entire flow happens automatically. Your push triggers everything.
    </Typography>
  </Box>,

  <Box key="en-4">
    <OnboardingVideo videoPath="Journey%202/Fase2%20_Step2/Video_Onboarding_Journey2_Phase2_Step2_EN.mp4" subtitlePath="Journey%202/Fase2%20_Step2/Legenda/Jornada2_Video1_Pipeline_EN.srt" />
    <Typography variant="body2" paragraph>
      <strong>Every pipeline follows the same three-stage structure:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Stage 1: Tests</strong><br />
      Runs your unit and integration tests, plus static code analysis with SonarQube. Coverage target: 95%. Note: SonarQube may show an initial ERROR that is just a WARNING while the project is being set up.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Stage 2: Build and push image</strong><br />
      Creates the Docker image and pushes it to the Azure Container Registry (ACR). The Dockerfile is maintained by the Platform Team.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Stage 3: Update version in GitOps</strong><br />
      Updates the bom.yaml file in the GitOps repository with the new image version for the corresponding environment.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      All pipeline stages must be passing. The entire team is responsible for keeping the CI pipeline green.
    </Typography>
  </Box>,

  <Box key="en-5">
    <OnboardingVideo videoPath="Journey%202/Fase2_Step3/Video_Onboarding_Journey2_Phase2_Step3_EN.mp4" subtitlePath="Journey%202/Fase2_Step3/Legenda/Jornada2_Video2_ArgoCD_Deploy_EN.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD takes your configuration from the GitOps repository and applies it to the Kubernetes cluster.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>How it works:</strong><br />
      ArgoCD watches two files: the bom.yaml (which image version to deploy) and the component-specific YAML file (how the app should run). Using Helm, it aggregates these values and creates a deployment package.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Auto Sync is enabled:</strong><br />
      In the current single ArgoCD instance, Auto Sync, Auto Prune, and Self Heal are enabled for all applications and environments. Changes are applied automatically.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      You do not need to manually sync. If a change is not reflecting, use Hard Refresh (covered in Phase 5).
    </Typography>
  </Box>,

  /* PHASE 3 - Configuring your application */
  <Box key="en-6">
    <OnboardingImage imagePath="images/Journey%202/Phase%203%20-%20Step%201/Frame.png" alt="Component file" />
    <Typography variant="body2" paragraph>
      Each microservice has its own YAML file inside each environment folder in the GitOps repository. This file defines how your application behaves in that specific environment.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Two required fields:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Name:</strong> Must match the YAML filename. Used for image generation and secrets/ConfigMaps.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      name: example-component
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>2. aadpodidbinding under labels:</strong> Ensures your component can access Azure resources. Pattern: &lt;system-name&gt;-identity-binding.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      labels:<br />
      &nbsp;&nbsp;aadpodidbinding: tersys-identity-binding
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Everything else (env vars, secrets, resources, health checks) is added on top of these two required fields.
    </Typography>
  </Box>,

  <Box key="en-7">
    <Typography variant="body2" paragraph>
      <strong>Non-sensitive values: env block</strong><br />
      For values like port numbers, log levels, and feature flags.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      env:<br />
      &nbsp;&nbsp;ENV_VAR_NAME: env-var-value<br />
      &nbsp;&nbsp;ENVIRONMENT: dev
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Never add sensitive information in this block.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Sensitive values: secrets block</strong><br />
      Reference values stored in Azure KeyVault.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      secrets:<br />
      &nbsp;&nbsp;- vaultName: nlardvtesysyuseakey<br />
      &nbsp;&nbsp;&nbsp;&nbsp;keys:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;nlardvtesysyuseasta-name: STORAGE_NAME<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;nlardvtesysyuseasta-primary-access-key: STORAGE_KEY
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Pattern: key-vault-secret-name: ENV_VAR_NAME (left = name in KeyVault, right = env var your app uses). You can reference multiple KeyVaults.
    </Typography>
  </Box>,

  <Box key="en-8">
    <Typography variant="body2" paragraph>
      <strong>CPU and memory: resources block</strong><br />
      Define limits and requests for your pod:
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      resources:<br />
      &nbsp;&nbsp;limits:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;cpu: 200m<br />
      &nbsp;&nbsp;&nbsp;&nbsp;memory: 256Mi<br />
      &nbsp;&nbsp;requests:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;cpu: 100m<br />
      &nbsp;&nbsp;&nbsp;&nbsp;memory: 128Mi
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Health checks: liveness and readiness</strong><br />
      Kubernetes uses these to know when your app is healthy and ready for traffic:
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      liveness:<br />
      &nbsp;&nbsp;enabled: true<br />
      &nbsp;&nbsp;probe:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;httpGet:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;path: /health<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;port: http
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Disabling health checks is strongly discouraged: it impacts Kubernetes self-healing and performance monitoring.
    </Typography>
  </Box>,

  <Box key="en-9">
    <Typography variant="body2" paragraph>
      Always validate your YAML before committing to the GitOps repository. Invalid YAML blocks the deploy immediately.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Why this matters:</strong><br />
      ArgoCD uses Helm to parse your YAML files. If the file has indentation errors, syntax issues, or structural problems, Helm cannot render the chart and the deploy fails with an error in the App Conditions section.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Before every commit:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Run your file through YAML Lint or a similar validator</Typography>
      <Typography variant="body2">• Check that indentation is consistent (spaces, not tabs)</Typography>
      <Typography variant="body2">• Verify all required fields are present (name, aadpodidbinding)</Typography>
      <Typography variant="body2">• Confirm no sensitive values are in the env block</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      YAML misconfiguration is the most common cause of deploy failures. A 30-second validation saves hours of debugging.
    </Typography>
  </Box>,

  /* PHASE 4 - Branches & going live */
  <Box key="en-10">
    <Typography variant="body2" paragraph>
      The environment that gets updated depends on which branch triggered the pipeline:
    </Typography>
    <OnboardingImage imagePath="images/Journey%202/Phase%204%20-%20Step%201/Frame.png" alt="Branch mapping" height={240} />
    <Typography variant="body2" paragraph>
      * pp only if the project has pre-production configured.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Hotfix/* branches</strong> must be created from main. They are short-lived, for urgent production fixes only.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Main does not deploy directly.</strong> It opens a Pull Request in the GitOps repository for the pr environment.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Important: The GitOps repository uses only the main branch. Never create other branches in it.
    </Typography>
  </Box>,

  <Box key="en-11">
    <OnboardingImage imagePath="images/Journey%202/Phase%204%20-%20Step%202/Frame.png" alt="Production flow" height={240} />
    <Typography variant="body2" paragraph>
      When your code is ready for production, the flow has an extra safety step:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Merge to main:</strong> The developer merges the approved branch into main in the application repository.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>2. Pipeline opens a Pull Request:</strong> It opens a PR in the GitOps repo for the pr environment.
      <br />Pattern: <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>release/&lt;component&gt;-&lt;version&gt;</code>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>3. Tech lead reviews and merges:</strong> This is the human checkpoint before production. The tech lead reviews the PR and approves.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>4. ArgoCD deploys to production:</strong> Once the PR is merged, ArgoCD detects the change and automatically deploys the new version to production.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      This PR-based flow ensures no code reaches production without review.
    </Typography>
  </Box>,

  <Box key="en-12">
    <Typography variant="body2" paragraph>
      As a developer on this platform, you can independently:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Write code and push to develop, release_candidate/*, or hotfix/* branches.</Typography>
      <Typography variant="body2">• Monitor the pipeline execution in GitHub Actions and check logs for failures.</Typography>
      <Typography variant="body2">• Edit your component file in the GitOps repository to configure environment variables and secret references.</Typography>
      <Typography variant="body2">• Monitor the deploy status in ArgoCD and verify the application is healthy.</Typography>
      <Typography variant="body2">• Query application logs in Grafana to validate that everything is working.</Typography>
      <Typography variant="body2">• Merge the PR opened by the pipeline in the GitOps repository to promote to production.</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      If something requires permissions you do not have, check with your Tech Lead or open a request to the Platform Team.
    </Typography>
  </Box>,

  /* PHASE 5 - Monitoring your deploys */
  <Box key="en-13">
    <OnboardingVideo videoPath="Journey%202/Fase5_Step1/Video_Onboarding_Journey2_Phase5_Step1_EN.mp4" subtitlePath="Journey%202/Fase5_Step1/Legenda/Jornada2_Video3_Checking_Deploys_EN.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD at <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>argocd-nlar.vsa.nestle.biz</code> shows the real-time status of every application.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Key information displayed:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Health Status:</strong> Healthy or Degraded</Typography>
      <Typography variant="body2">• <strong>Sync Status:</strong> Synced or OutOfSync</Typography>
      <Typography variant="body2">• <strong>Resource Tree:</strong> Visual tree of all Kubernetes resources</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Finding your applications:</strong><br />
      Use label filters: <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=dv</code> for development, <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=ts</code> for test, <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=pr</code> for production.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>If a change is not reflecting:</strong><br />
      Use Hard Refresh: click the Refresh button and select "Hard Refresh". This forces ArgoCD to fetch the latest data. Sync happens automatically after.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      If you cannot see any applications, you may need to request access.
    </Typography>
  </Box>,

  <Box key="en-14">
    <OnboardingVideo videoPath="Journey%202/Fase5_Step2/Video_Onboarding_Journey2_Phase5_Step2_EN.mp4" subtitlePath="Journey%202/Fase5_Step2/Legenda/Jornada2_Video4_Logs_Grafana_EN.srt" />
    <Typography variant="body2" paragraph>
      Grafana shows your application logs by environment:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Grafana instances:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Development:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-dv.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Test/Homolog:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-ts.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Production:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana.vsa.nestle.biz</code></Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Querying logs:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Open the Explore section in the left sidebar</Typography>
      <Typography variant="body2">2. Confirm the Datasource is correct (top left)</Typography>
      <Typography variant="body2">3. Choose Builder mode (UI filters) or Code mode (direct LogQL query)</Typography>
      <Typography variant="body2">4. Click "Run Query" to see results</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Organizations:</strong><br />
      Each team has its own Organization in Grafana, ensuring isolated access to logs and data. Check the top-left corner to verify your Organization.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      <strong>Caution:</strong> Avoid querying long time ranges. They may cause timeouts or overload the Kubernetes clusters.
    </Typography>
  </Box>,

  <Box key="en-15">
    <Box style={{ textAlign: 'center', paddingTop: 16 }}>
      <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
      <Typography variant="h6">Journey 2 complete!</Typography>
    </Box>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      Congratulations! You have completed Journey 2: Code to Deploy.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>You now understand:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• How your code goes from a push to a running application through the CI/CD pipeline</Typography>
      <Typography variant="body2">• How to configure your application in the GitOps repository with env vars, secrets, resources, and health checks</Typography>
      <Typography variant="body2">• How different branches promote code to different environments, and how the production flow works through a reviewed Pull Request</Typography>
      <Typography variant="body2">• How to monitor deploys in ArgoCD and check logs in Grafana</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      <strong>What comes next:</strong><br />
      The Playground will be unlocked for you to practice everything hands-on in a safe environment. Follow the instructions on the Playground screen to complete your practical validation.
    </Typography>
  </Box>,
];

/* ================================================================
 *  Journey 2 — Portuguese pt-BR (15 steps)
 * ================================================================ */

const getJourney2ContentPtBR = (c: OnboardingColors): React.ReactNode[] => [
  /* PHASE 1: Antes de começar */
  <Box key="pt-1">
    <OnboardingImage imagePath="images/Journey%202/Phase%201%20-%20Step%201/Frame.png" alt="Dois repositórios" height={280} />
    <Typography variant="body2" paragraph>
      <strong>Seu projeto usa dois repositórios que funcionam juntos:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Repositório de aplicação (GitHub)</strong><br />
      Aqui é onde seu código vive. Você escreve features, corrige bugs e envia mudanças aqui.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Repositório GitOps</strong><br />
      Aqui é onde fica a configuração de como sua aplicação é executada em cada ambiente: variáveis de ambiente, secrets, limites de CPU/memória e health checks.
    </Typography>
    <Typography variant="body2" paragraph>
      O repositório GitOps é organizado por ambiente:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="caption">dv/ → Development</Typography><br />
      <Typography variant="caption">ts/ → Test / Homolog</Typography><br />
      <Typography variant="caption">pp/ → Pre-production</Typography><br />
      <Typography variant="caption">pr/ → Production</Typography>
    </Typography>
  </Box>,

  <Box key="pt-2">
    <Typography variant="body2" paragraph>
      <strong>Dockerfiles</strong><br />
      Criados e mantidos pelo Time da Plataforma. Não armazenados em seu repositório. Mesmo se você adicionar seu próprio Dockerfile, a pipeline vai ignorá-lo.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Templates de pipeline</strong><br />
      Todas as pipelines de CI/CD são pré-configuradas usando templates que suportam múltiplas linguagens. Cada aplicação vem com uma configuração de pipeline pronta para usar.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Helm charts</strong><br />
      Usados pelo ArgoCD para empacotar e fazer deploy da sua aplicação. Você configura seu arquivo de componente; Helm cuida do resto.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Sua responsabilidade: escrever código de aplicação, configurar seus arquivos de componente no repositório GitOps, e monitorar seus deployments.
    </Typography>
  </Box>,

  /* PHASE 2: A pipeline CI/CD */
  <Box key="pt-3">
    <OnboardingImage imagePath="images/Journey%202/Phase%202%20-%20Step%201/Frame.png" alt="Fluxo completo" height={280} />
    <Typography variant="body2" paragraph>
      <strong>Aqui está o que acontece de ponta a ponta quando você envia código:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Você envia para a branch develop</strong> no repositório de aplicação no GitHub.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>2. A pipeline de CI é executada automaticamente</strong> - Três estágios executam em sequência: Testes, Build e Atualização GitOps.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>3. O repositório GitOps é atualizado</strong> - A pipeline atualiza o arquivo bom.yaml com a nova versão de imagem.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>4. O ArgoCD detecta a mudança</strong> - ArgoCD automaticamente faz deploy da nova versão para o cluster Kubernetes do ambiente dv (Kds).
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      Todo este fluxo acontece automaticamente. Seu push ativa tudo.
    </Typography>
  </Box>,

  <Box key="pt-4">
    <OnboardingVideo videoPath="Journey%202/Fase2%20_Step2/Video_Onboarding_Journey2_Phase2_Step2_PT.mp4" subtitlePath="Journey%202/Fase2%20_Step2/Legenda/Jornada2_Video1_Pipeline_PT.srt" />
    <Typography variant="body2" paragraph>
      <strong>Toda pipeline segue a mesma estrutura de três estágios:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Estágio 1: Testes</strong><br />
      Executa seus testes unitários e de integração, mais análise de código estática com SonarQube. Meta de cobertura: 95%. Nota: SonarQube pode mostrar um ERROR inicial que é apenas um WARNING enquanto o projeto está configuração.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Estágio 2: Build e push de imagem</strong><br />
      Cria a imagem Docker e a envia para o Azure Container Registry (ACR). O Dockerfile é mantido pelo Time da Plataforma.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Estágio 3: Atualizar versão no GitOps</strong><br />
      Atualiza o arquivo bom.yaml no repositório GitOps com a nova versão de imagem para o ambiente correspondente.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Todos os estágios da pipeline devem estar passando. Todo o time é responsável por manter a pipeline de CI verde.
    </Typography>
  </Box>,

  <Box key="pt-5">
    <OnboardingVideo videoPath="Journey%202/Fase2_Step3/Video_Onboarding_Journey2_Phase2_Step3_PT.mp4" subtitlePath="Journey%202/Fase2_Step3/Legenda/Jornada2_Video2_ArgoCD_Deploy_PT.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD pega sua configuração do repositório GitOps e aplica ao cluster Kubernetes.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Como funciona:</strong><br />
      ArgoCD observa dois arquivos: o bom.yaml (qual versão de imagem fazer deploy) e o arquivo YAML específico do componente (como a aplicação deve ser executada). Usando Helm, agrega esses valores e cria um pacote de deployment.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Auto Sync está ativado:</strong><br />
      Na instância atual única de ArgoCD, Auto Sync, Auto Prune e Self Heal estão habilitados para todas as aplicações e ambientes. Mudanças são aplicadas automaticamente.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Você não precisa fazer sync manual. Se uma mudança não está se refletindo, use Hard Refresh (coberto na Fase 5).
    </Typography>
  </Box>,

  /* PHASE 3 - Configurando sua aplicação */
  <Box key="pt-6">
    <OnboardingImage imagePath="images/Journey%202/Phase%203%20-%20Step%201/Frame.png" alt="Arquivo de componente" />
    <Typography variant="body2" paragraph>
      Cada microserviço tem seu próprio arquivo YAML dentro de cada pasta de ambiente no repositório GitOps. Este arquivo define como sua aplicação se comporta naquele ambiente específico.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Dois campos obrigatórios:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Name:</strong> Deve corresponder ao nome do arquivo YAML. Usado para geração de imagens e secrets/ConfigMaps.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      name: example-component
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>2. aadpodidbinding sob labels:</strong> Garante que seu componente possa acessar recursos Azure. Padrão: &lt;nome-do-sistema&gt;-identity-binding.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      labels:<br />
      &nbsp;&nbsp;aadpodidbinding: tersys-identity-binding
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Tudo mais (variáveis de ambiente, secrets, recursos, health checks) é adicionado sobre esses dois campos obrigatórios.
    </Typography>
  </Box>,

  <Box key="pt-7">
    <Typography variant="body2" paragraph>
      <strong>Valores não-sensíveis: bloco env</strong><br />
      Para valores como números de porta, níveis de log e feature flags.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      env:<br />
      &nbsp;&nbsp;ENV_VAR_NAME: env-var-value<br />
      &nbsp;&nbsp;ENVIRONMENT: dev
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Nunca adicione informações sensíveis neste bloco.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Valores sensíveis: bloco secrets</strong><br />
      Valores de referência armazenados no Azure KeyVault.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      secrets:<br />
      &nbsp;&nbsp;- vaultName: nlardvtesysyuseakey<br />
      &nbsp;&nbsp;&nbsp;&nbsp;keys:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;nlardvtesysyuseasta-name: STORAGE_NAME<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;nlardvtesysyuseasta-primary-access-key: STORAGE_KEY
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Padrão: nome-do-secreto-keyvault: NOME_VARIÁVEL (esquerda = nome no KeyVault, direita = variável de ambiente que seu app usa). Você pode referenciar múltiplos KeyVaults.
    </Typography>
  </Box>,

  <Box key="pt-8">
    <Typography variant="body2" paragraph>
      <strong>CPU e memória: bloco resources</strong><br />
      Define limites e requisições para seu pod:
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      resources:<br />
      &nbsp;&nbsp;limits:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;cpu: 200m<br />
      &nbsp;&nbsp;&nbsp;&nbsp;memory: 256Mi<br />
      &nbsp;&nbsp;requests:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;cpu: 100m<br />
      &nbsp;&nbsp;&nbsp;&nbsp;memory: 128Mi
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Health checks: liveness e readiness</strong><br />
      Kubernetes usa esses para saber quando seu app está saudável e pronto para tráfego:
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      liveness:<br />
      &nbsp;&nbsp;enabled: true<br />
      &nbsp;&nbsp;probe:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;httpGet:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;path: /health<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;port: http
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Desabilitar health checks é fortemente desaconselhado: impacta auto-recuperação do Kubernetes e monitoramento de performance.
    </Typography>
  </Box>,

  <Box key="pt-9">
    <Typography variant="body2" paragraph>
      Sempre valide seu YAML antes de fazer commit no repositório GitOps. YAML inválido bloqueia o deploy imediatamente.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Por que isso importa:</strong><br />
      ArgoCD usa Helm para fazer parse dos seus arquivos YAML. Se o arquivo tem erros de indentação, problemas de sintaxe ou estruturais, Helm não consegue renderizar o chart e o deploy falha com um erro na seção App Conditions.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Antes de cada commit:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Execute seu arquivo através de YAML Lint ou validador similar</Typography>
      <Typography variant="body2">• Verifique que indentação é consistente (espaços, não tabs)</Typography>
      <Typography variant="body2">• Valide que todos os campos obrigatórios estão presentes (name, aadpodidbinding)</Typography>
      <Typography variant="body2">• Confirme que não há valores sensíveis no bloco env</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Configuração incorreta de YAML é a causa mais comum de falhas de deploy. Uma validação de 30 segundos economiza horas de debugging.
    </Typography>
  </Box>,

  /* PHASE 4 - Branches & going live (PT-BR) */
  <Box key="pt-10">
    <Typography variant="body2" paragraph>
      O ambiente que é atualizado depende de qual branch acionou a pipeline:
    </Typography>
    <OnboardingImage imagePath="images/Journey%202/Phase%204%20-%20Step%201/Frame.png" alt="Mapeamento de branches" height={240} />
    <Typography variant="body2" paragraph>
      * pp apenas se o projeto tem pré-produção configurada.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Branches hotfix/*</strong> devem ser criadas a partir da main. Elas são de vida curta, apenas para correções urgentes de produção.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Main não faz deploy direto.</strong> Ela abre um Pull Request no repositório GitOps para o ambiente pr.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Importante: O repositório GitOps usa apenas a branch main. Nunca crie outras branches nele.
    </Typography>
  </Box>,

  <Box key="pt-11">
    <OnboardingImage imagePath="images/Journey%202/Phase%204%20-%20Step%202/Frame.png" alt="Fluxo de produção" height={240} />
    <Typography variant="body2" paragraph>
      Quando seu código está pronto para produção, o fluxo tem um passo extra de segurança:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Merge para main:</strong> O desenvolvedor faz merge da branch aprovada para main no repositório de aplicação.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>2. Pipeline abre um Pull Request:</strong> Abre um PR no repositório GitOps para o ambiente pr.
      <br />Padrão: <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>release/&lt;componente&gt;-&lt;versão&gt;</code>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>3. Tech lead revisa e faz merge:</strong> Este é o checkpoint humano antes de produção. O tech lead revisa o PR e aprova.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>4. ArgoCD faz deploy em produção:</strong> Uma vez que o PR é feito merge, ArgoCD detecta a mudança e automaticamente faz deploy da nova versão para produção.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      Este fluxo baseado em PR garante que nenhum código chegue a produção sem revisão.
    </Typography>
  </Box>,

  <Box key="pt-12">
    <Typography variant="body2" paragraph>
      Como desenvolvedor nesta plataforma, você pode independentemente:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Escrever código e enviar para branches develop, release_candidate/*, ou hotfix/*.</Typography>
      <Typography variant="body2">• Monitorar a execução da pipeline no GitHub Actions e verificar logs de falhas.</Typography>
      <Typography variant="body2">• Editar seu arquivo de componente no repositório GitOps para configurar variáveis de ambiente e referências de secrets.</Typography>
      <Typography variant="body2">• Monitorar o status do deploy em ArgoCD e verificar se a aplicação está saudável.</Typography>
      <Typography variant="body2">• Consultar logs da aplicação no Grafana para validar que tudo está funcionando.</Typography>
      <Typography variant="body2">• Fazer merge do PR aberto pela pipeline no repositório GitOps para promover a produção.</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Se algo requer permissões que você não tem, verifique com seu Tech Lead ou abra uma solicitação para o Time da Plataforma.
    </Typography>
  </Box>,

  /* PHASE 5 - Monitorando seus deploys (PT-BR) */
  <Box key="pt-13">
    <OnboardingVideo videoPath="Journey%202/Fase5_Step1/Video_Onboarding_Journey2_Phase5_Step1_PT.mp4" subtitlePath="Journey%202/Fase5_Step1/Legenda/Jornada2_Video3_Checking_Deploys_PT.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD em <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>argocd-nlar.vsa.nestle.biz</code> mostra o status em tempo real de cada aplicação.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Informação chave exibida:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Health Status:</strong> Healthy ou Degraded</Typography>
      <Typography variant="body2">• <strong>Sync Status:</strong> Synced ou OutOfSync</Typography>
      <Typography variant="body2">• <strong>Resource Tree:</strong> Árvore visual de todos os recursos Kubernetes</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Encontrando suas aplicações:</strong><br />
      Use filtros de label: <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=dv</code> para desenvolvimento, <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=ts</code> para teste, <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=pr</code> para produção.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Se uma alteração não está se refletindo:</strong><br />
      Use Hard Refresh: clique no botão Refresh e selecione "Hard Refresh". Isso força o ArgoCD a buscar os dados mais recentes. Sincronização acontece automaticamente após.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Se você não consegue ver nenhuma aplicação, você pode precisar solicitar acesso.
    </Typography>
  </Box>,

  <Box key="pt-14">
    <OnboardingVideo videoPath="Journey%202/Fase5_Step2/Video_Onboarding_Journey2_Phase5_Step2_PT.mp4" subtitlePath="Journey%202/Fase5_Step2/Legenda/Jornada2_Video4_Logs_Grafana_PT.srt" />
    <Typography variant="body2" paragraph>
      Grafana mostra seus logs de aplicação por ambiente:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Instâncias Grafana:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Desenvolvimento:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-dv.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Teste/Homolog:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-ts.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Produção:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana.vsa.nestle.biz</code></Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Consultando logs:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Abra a seção Explore na barra esquerda</Typography>
      <Typography variant="body2">2. Confirme que o Datasource está correto (canto superior esquerdo)</Typography>
      <Typography variant="body2">3. Escolha modo Builder (filtros UI) ou modo Code (query LogQL direto)</Typography>
      <Typography variant="body2">4. Clique "Run Query" para ver resultados</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Organizações:</strong><br />
      Cada time tem sua própria Organization em Grafana, garantindo acesso isolado aos logs e dados. Verifique o canto superior esquerdo para confirmar sua Organization.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      <strong>Cautela:</strong> Evite consultar intervalos de tempo longos. Eles podem causar timeouts ou sobrecarregar os clusters Kubernetes.
    </Typography>
  </Box>,

  <Box key="pt-15">
    <Box style={{ textAlign: 'center', paddingTop: 16 }}>
      <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
      <Typography variant="h6">Jornada 2 completa!</Typography>
    </Box>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      Parabéns! Você completou a Jornada 2: Código para Deploy.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Você agora entende:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Como seu código vai de um push para uma aplicação em execução através do pipeline CI/CD</Typography>
      <Typography variant="body2">• Como configurar sua aplicação no repositório GitOps com variáveis de ambiente, secrets, recursos e health checks</Typography>
      <Typography variant="body2">• Como diferentes branches promovem código para diferentes ambientes, e como o fluxo de produção funciona através de um Pull Request revisado</Typography>
      <Typography variant="body2">• Como monitorar deploys em ArgoCD e verificar logs em Grafana</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      <strong>O que vem a seguir:</strong><br />
      O Playground será desbloqueado para você práticar tudo hands-on em um ambiente seguro. Siga as instruções na tela do Playground para completar sua validação prática.
    </Typography>
  </Box>,
];

/* ================================================================
 *  Journey 2 — Spanish (15 steps)
 * ================================================================ */

const getJourney2ContentES = (c: OnboardingColors): React.ReactNode[] => [
  /* PHASE 1: Antes de empezar */
  <Box key="es-1">
    <OnboardingImage imagePath="images/Journey%202/Phase%201%20-%20Step%201/Frame.png" alt="Dos repositorios" height={280} />
    <Typography variant="body2" paragraph>
      <strong>Tu proyecto usa dos repositorios que funcionan juntos:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Repositorio de aplicación (GitHub)</strong><br />
      Aquí es donde vive tu código. Escribes features, corriges bugs y envías cambios aquí.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Repositorio GitOps</strong><br />
      Aquí es donde está la configuración de cómo tu aplicación se ejecuta en cada ambiente: variables de entorno, secretos, límites de CPU/memoria y health checks.
    </Typography>
    <Typography variant="body2" paragraph>
      El repositorio GitOps está organizado por ambiente:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="caption">dv/ → Development</Typography><br />
      <Typography variant="caption">ts/ → Test / Homolog</Typography><br />
      <Typography variant="caption">pp/ → Pre-production</Typography><br />
      <Typography variant="caption">pr/ → Production</Typography>
    </Typography>
  </Box>,

  <Box key="es-2">
    <Typography variant="body2" paragraph>
      <strong>Dockerfiles</strong><br />
      Creados y mantenidos por el Equipo de Plataforma. No se almacenan en tu repositorio. Incluso si añades tu propio Dockerfile, el pipeline lo ignorará.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Templates de pipeline</strong><br />
      Todo los pipelines de CI/CD se pre-configuran usando templates que soportan múltiples lenguajes. Cada aplicación viene con una configuración de pipeline lista para usar.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Helm charts</strong><br />
      Usados por ArgoCD para empaquetar e implementar tu aplicación. Configuras tu archivo de componente; Helm maneja el resto.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Tu responsabilidad: escribe código de aplicación, configura tus archivos de componente en el repositorio de GitOps, y monitorea tus deployments.
    </Typography>
  </Box>,

  /* PHASE 2: El pipeline CI/CD */
  <Box key="es-3">
    <OnboardingImage imagePath="images/Journey%202/Phase%202%20-%20Step%201/Frame.png" alt="Flujo completo" height={280} />
    <Typography variant="body2" paragraph>
      <strong>Aquí está lo que sucede de punta a punta cuando envías código:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Envías a la rama develop</strong> en el repositorio de aplicación en GitHub.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>2. El pipeline de CI se ejecuta automáticamente</strong> - Tres etapas se ejecutan en secuencia: Pruebas, Build y Actualización GitOps.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>3. El repositorio GitOps se actualiza</strong> - El pipeline actualiza el archivo bom.yaml con la nueva versión de imagen.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>4. ArgoCD detecta el cambio</strong> - ArgoCD automáticamente implementa la nueva versión en el cluster Kubernetes del ambiente dv (Kds).
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      Este flujo completo ocurre automáticamente. Tu envío activa todo.
    </Typography>
  </Box>,

  <Box key="es-4">
    <OnboardingVideo videoPath="Journey%202/Fase2%20_Step2/Video_Onboarding_Journey2_Phase2_Step2_ES.mp4" subtitlePath="Journey%202/Fase2%20_Step2/Legenda/Jornada2_Video1_Pipeline_ES.srt" />
    <Typography variant="body2" paragraph>
      <strong>Todo pipeline sigue la misma estructura de tres etapas:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Etapa 1: Pruebas</strong><br />
      Ejecuta tus pruebas unitarias e integración, más análisis de código estático con SonarQube. Meta de cobertura: 95%. Nota: SonarQube puede mostrar un ERROR inicial que es solo una ADVERTENCIA mientras el proyecto se configura.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Etapa 2: Build y push de imagen</strong><br />
      Crea la imagen Docker y la envía al Azure Container Registry (ACR). El Dockerfile es mantenido por el Equipo de Plataforma.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Etapa 3: Actualizar versión en GitOps</strong><br />
      Actualiza el archivo bom.yaml en el repositorio GitOps con la nueva versión de imagen para el ambiente correspondiente.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Todos los pasos del pipeline deben estar pasando. Todo el equipo es responsable de mantener el pipeline de CI verde.
    </Typography>
  </Box>,

  <Box key="es-5">
    <OnboardingVideo videoPath="Journey%202/Fase2_Step3/Video_Onboarding_Journey2_Phase2_Step3_ES.mp4" subtitlePath="Journey%202/Fase2_Step3/Legenda/Jornada2_Video2_ArgoCD_Deploy_ES.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD toma tu configuración del repositorio GitOps y la aplica al cluster Kubernetes.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Cómo funciona:</strong><br />
      ArgoCD vigila dos archivos: el bom.yaml (qué versión de imagen implementar) y el archivo YAML específico del componente (cómo la aplicación debe ejecutarse). Usando Helm, agrega estos valores y crea un paquete de implementación.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Auto Sync está habilitado:</strong><br />
      En la instancia única actual de ArgoCD, Auto Sync, Auto Prune y Self Heal están habilitados para todas las aplicaciones y ambientes. Los cambios se aplican automáticamente.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      No necesitas sincronizar manualmente. Si un cambio no se refleja, usa Hard Refresh (cubierto en Fase 5).
    </Typography>
  </Box>,

  /* PHASE 3 - Configurando tu aplicación */
  <Box key="es-6">
    <OnboardingImage imagePath="images/Journey%202/Phase%203%20-%20Step%201/Frame.png" alt="Archivo de componente" />
    <Typography variant="body2" paragraph>
      Cada microservicio tiene su propio archivo YAML dentro de cada carpeta de ambiente en el repositorio GitOps. Este archivo define cómo tu aplicación se comporta en ese ambiente específico.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Dos campos requeridos:</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Name:</strong> Debe coincidir con el nombre del archivo YAML. Se usa para generación de imágenes y secrets/ConfigMaps.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      name: example-component
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>2. aadpodidbinding bajo labels:</strong> Asegura que tu componente puede acceder a recursos Azure. Patrón: &lt;nombre-del-sistema&gt;-identity-binding.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      labels:<br />
      &nbsp;&nbsp;aadpodidbinding: tersys-identity-binding
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Todo lo demás (variables de entorno, secrets, recursos, health checks) se agrega encima de estos dos campos requeridos.
    </Typography>
  </Box>,

  <Box key="es-7">
    <Typography variant="body2" paragraph>
      <strong>Valores no sensibles: bloque env</strong><br />
      Para valores como números de puerto, niveles de registro y feature flags.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      env:<br />
      &nbsp;&nbsp;ENV_VAR_NAME: env-var-value<br />
      &nbsp;&nbsp;ENVIRONMENT: dev
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Nunca agregues información sensible en este bloque.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Valores sensibles: bloque secrets</strong><br />
      Valores de referencia almacenados en Azure KeyVault.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      secrets:<br />
      &nbsp;&nbsp;- vaultName: nlardvtesysyuseakey<br />
      &nbsp;&nbsp;&nbsp;&nbsp;keys:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;nlardvtesysyuseasta-name: STORAGE_NAME<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;nlardvtesysyuseasta-primary-access-key: STORAGE_KEY
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Patrón: nombre-secreto-keyvault: NOMBRE_VARIABLE (izquierda = nombre en KeyVault, derecha = variable de entorno que tu app usa). Puedes referenciar múltiples KeyVaults.
    </Typography>
  </Box>,

  <Box key="es-8">
    <Typography variant="body2" paragraph>
      <strong>CPU y memoria: bloque resources</strong><br />
      Define límites y solicitudes para tu pod:
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      resources:<br />
      &nbsp;&nbsp;limits:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;cpu: 200m<br />
      &nbsp;&nbsp;&nbsp;&nbsp;memory: 256Mi<br />
      &nbsp;&nbsp;requests:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;cpu: 100m<br />
      &nbsp;&nbsp;&nbsp;&nbsp;memory: 128Mi
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Health checks: liveness y readiness</strong><br />
      Kubernetes usa estos para saber cuándo tu app está saludable y lista para tráfico:
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg, padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem', marginTop: 8 }}>
      liveness:<br />
      &nbsp;&nbsp;enabled: true<br />
      &nbsp;&nbsp;probe:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;httpGet:<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;path: /health<br />
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;port: http
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Deshabilitar health checks es fuertemente desaconsejado: impacta la auto-recuperación de Kubernetes y el monitoreo de rendimiento.
    </Typography>
  </Box>,

  <Box key="es-9">
    <Typography variant="body2" paragraph>
      Siempre valida tu YAML antes de hacer commit en el repositorio GitOps. YAML inválido bloquea el despliegue inmediatamente.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Por qué importa:</strong><br />
      ArgoCD usa Helm para analizar tus archivos YAML. Si el archivo tiene errores de indentación, problemas de sintaxis o estructura, Helm no puede renderizar el chart y el despliegue falla con un error en la sección App Conditions.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Antes de cada commit:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Ejecuta tu archivo a través de YAML Lint o un validador similar</Typography>
      <Typography variant="body2">• Verifica que la indentación sea consistente (espacios, no tabs)</Typography>
      <Typography variant="body2">• Valida que todos los campos requeridos estén presentes (name, aadpodidbinding)</Typography>
      <Typography variant="body2">• Confirma que no hay valores sensibles en el bloque env</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      La configuración incorrecta de YAML es la causa más común de fallos de despliegue. Una validación de 30 segundos ahorra horas de depuración.
    </Typography>
  </Box>,

  /* PHASE 4 - Branches & going live (ES) */
  <Box key="es-10">
    <Typography variant="body2" paragraph>
      El ambiente que se actualiza depende de qué rama activó el pipeline:
    </Typography>
    <OnboardingImage imagePath="images/Journey%202/Phase%204%20-%20Step%201/Frame.png" alt="Mapeo de ramas" height={240} />
    <Typography variant="body2" paragraph>
      * pp solo si el proyecto tiene pre-producción configurada.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Las ramas hotfix/*</strong> deben crearse desde main. Son de corta duración, solo para arreglos urgentes de producción.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Main no se despliega directamente.</strong> Abre un Pull Request en el repositorio GitOps para el ambiente pr.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Importante: El repositorio GitOps usa solo la rama main. Nunca crees otras ramas en él.
    </Typography>
  </Box>,

  <Box key="es-11">
    <OnboardingImage imagePath="images/Journey%202/Phase%204%20-%20Step%202/Frame.png" alt="Flujo de producción" height={240} />
    <Typography variant="body2" paragraph>
      Cuando tu código está listo para producción, el flujo tiene un paso adicional de seguridad:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>1. Merge a main:</strong> El desarrollador hace merge de la rama aprobada a main en el repositorio de aplicación.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>2. El pipeline abre un Pull Request:</strong> Abre un PR en el repositorio GitOps para el ambiente pr.
      <br />Patrón: <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>release/&lt;componente&gt;-&lt;versión&gt;</code>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>3. Tech lead revisa y hace merge:</strong> Este es el checkpoint humano antes de producción. El tech lead revisa el PR y aprueba.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>4. ArgoCD se despliega en producción:</strong> Una vez que el PR se fusiona, ArgoCD detecta el cambio e implementa automáticamente la nueva versión en producción.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      Este flujo basado en PR garantiza que ningún código llegue a producción sin revisión.
    </Typography>
  </Box>,

  <Box key="es-12">
    <Typography variant="body2" paragraph>
      Como desarrollador en esta plataforma, puedes independientemente:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Escribir código e implementar cambios en ramas develop, release_candidate/*, o hotfix/*.</Typography>
      <Typography variant="body2">• Monitorear la ejecución del pipeline en GitHub Actions y verificar logs de fallos.</Typography>
      <Typography variant="body2">• Editar tu archivo de componente en el repositorio GitOps para configurar variables de entorno y referencias de secretos.</Typography>
      <Typography variant="body2">• Monitorear el estado de implementación en ArgoCD y verificar que la aplicación esté sana.</Typography>
      <Typography variant="body2">• Consultar logs de aplicación en Grafana para validar que todo está funcionando.</Typography>
      <Typography variant="body2">• Hacer merge del PR abierto por el pipeline en el repositorio GitOps para llegar a producción.</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Si algo requiere permisos que no tienes, consulta con tu Tech Lead o abre una solicitud al Equipo de Plataforma.
    </Typography>
  </Box>,

  /* PHASE 5 - Monitoreando tus despliegues (ES) */
  <Box key="es-13">
    <OnboardingVideo videoPath="Journey%202/Fase5_Step1/Video_Onboarding_Journey2_Phase5_Step1_ES.mp4" subtitlePath="Journey%202/Fase5_Step1/Legenda/Jornada2_Video3_Checking_Deploys_ES.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD en <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>argocd-nlar.vsa.nestle.biz</code> muestra el estado en tiempo real de cada aplicación.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Información clave mostrada:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Health Status:</strong> Healthy o Degraded</Typography>
      <Typography variant="body2">• <strong>Sync Status:</strong> Synced u OutOfSync</Typography>
      <Typography variant="body2">• <strong>Resource Tree:</strong> Árbol visual de todos los recursos de Kubernetes</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Encontrando tus aplicaciones:</strong><br />
      Usa filtros de etiqueta: <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=dv</code> para desarrollo, <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=ts</code> para prueba, <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>env=pr</code> para producción.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Si un cambio no se refleja:</strong><br />
      Usa Hard Refresh: haz clic en el botón Refresh y selecciona "Hard Refresh". Esto obliga a ArgoCD a buscar los datos más recientes. La sincronización ocurre automáticamente después.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Si no puedes ver ninguna aplicación, es posible que debas solicitar acceso.
    </Typography>
  </Box>,

  <Box key="es-14">
    <OnboardingVideo videoPath="Journey%202/Fase5_Step2/Video_Onboarding_Journey2_Phase5_Step2_ES.mp4" subtitlePath="Journey%202/Fase5_Step2/Legenda/Jornada2_Video4_Logs_Grafana_ES.srt" />
    <Typography variant="body2" paragraph>
      Grafana muestra tus logs de aplicación por ambiente:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Instancias de Grafana:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Desarrollo:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-dv.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Prueba/Homolog:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-ts.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Producción:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana.vsa.nestle.biz</code></Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Consultando logs:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Abre la sección Explore en la barra izquierda</Typography>
      <Typography variant="body2">2. Confirma que el Datasource es correcto (esquina superior izquierda)</Typography>
      <Typography variant="body2">3. Elige modo Builder (filtros UI) o modo Code (consulta LogQL directa)</Typography>
      <Typography variant="body2">4. Haz clic en "Run Query" para ver resultados</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Organizaciones:</strong><br />
      Cada equipo tiene su propia Organización en Grafana, garantizando acceso aislado a logs y datos. Verifica la esquina superior izquierda para confirmar tu Organización.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      <strong>Precaución:</strong> Evita consultar rangos de tiempo largos. Pueden causar timeouts o sobrecargar los clusters de Kubernetes.
    </Typography>
  </Box>,

  <Box key="es-15">
    <Box style={{ textAlign: 'center', paddingTop: 16 }}>
      <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
      <Typography variant="h6">¡Jornada 2 completada!</Typography>
    </Box>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      ¡Felicidades! Has completado la Jornada 2: Código hacia el despliegue.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Ahora entiendes:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Cómo tu código va de un push a una aplicación en ejecución a través del pipeline CI/CD</Typography>
      <Typography variant="body2">• Cómo configurar tu aplicación en el repositorio GitOps con variables de entorno, secretos, recursos y health checks</Typography>
      <Typography variant="body2">• Cómo diferentes ramas promueven código a diferentes ambientes, y cómo el flujo de producción funciona a través de un Pull Request revisado</Typography>
      <Typography variant="body2">• Cómo monitorear despliegues en ArgoCD y verificar logs en Grafana</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      <strong>Lo siguiente:</strong><br />
      El Playground se desbloqueará para que practiques todo hands-on en un entorno seguro. Sigue las instrucciones en la pantalla del Playground para completar tu validación práctica.
    </Typography>
  </Box>,
];
